import { DocActionTypes } from "../actions/doc.actions";
import produce from "immer";
import {
  Doc,
  View,
} from "src/app/features/designer-studio/edit-pdf-doc/models/doc.model";
import { Config } from "src/app/features/designer-studio/settings/settings.model";
import { ROUTER_NAVIGATED } from "@ngrx/router-store";
import { SelectedService } from "../../../shared/models/Service.model";
import { ComponentType } from "src/app/modules/jha-screen/model/Component.model";

export const DOC_FEATURE_KEY = "editPdfDoc";

export interface EditFormState {
  formLoading: boolean;
  pageCount: number;
  loadDocFailure: boolean;
  currentViewName: string;
  currentSelectedFieldId: string;
  currentSelectedRadioId: string;
  config: Config;
  currentService: SelectedService;
  views: { [key: string]: View };
  doc: Doc;
  hasChanges: boolean;
  htmlElements: any[];
}

export const initialState: EditFormState = {
  formLoading: true,
  pageCount: 0,
  loadDocFailure: false,
  currentViewName: null,
  currentService: null,
  currentSelectedFieldId: null,
  currentSelectedRadioId: null,
  config: null,
  views: null,
  doc: null,
  hasChanges: false,
  htmlElements: [],
};

export const docReducer = produce((draft, action) => {
  switch (action.type) {
    case ROUTER_NAVIGATED:
      if (action.payload.routerState.params.viewId !== draft.currentViewName) {
        draft.currentSelectedFieldId = null;
        draft.currentService = null;
        draft.currentViewName = action.payload.routerState.params.viewId;
      }
      return;

    case DocActionTypes.SET_STATE: {
      const newDoc = structuredClone(action.state.doc);
    }

    case DocActionTypes.SET_DOC: {
      // we move views up a level and store them by key
      // for easier manipulation. The views are added back to the correct place on save
      const newDoc = structuredClone(action.doc);
      if (newDoc.views) {
        const viewMap = {};
        newDoc.views.forEach((view: View) => {
          if (view && view.viewType === "POPUP") {
            // For backward compatibility - in case stageComponents top position does not
            // match html element position, update it
            view?.popupDetails?.data?.forEach((component) => {
              const html = view.popupDetails?.html;
              if (!html) return;
              const parser = new DOMParser();
              const doc = parser.parseFromString(html, "text/html");
              const element = doc.getElementById(component.id);
              if (element) {
                const top = Number(element.style.top.replace("px", ""));
                const left = Number(element.style.left.replace("px", ""));
                if (component.top !== top || component.left !== left) {
                  component.top = top;
                  component.left = left;
                }
                if (component.type === ComponentType.Checkbox) {
                  component.inner_text = component.inner_text || component.name;
                }
              }
            });
          }
          viewMap[view.viewName] = view;
        });
        newDoc.views = null;
        draft.views = viewMap;
      }

      draft.doc = newDoc;
      return;
    }

    case DocActionTypes.SET_CONFIG:
      draft.config = action.config;
      return;

    case DocActionTypes.UPDATE_FUNCTIONS:
      draft.doc.jsFunctions = action.funcs;
      return;

    case DocActionTypes.UPDATE_VARIABLES:
      draft.doc.jsVariables = action.vars;
      return;

    case DocActionTypes.SET_FUNCTIONS:
      draft.doc.jsFunctions = action.funcs;
      return;

    case DocActionTypes.SET_VARIABLES:
      draft.doc.jsVariables = action.vars;
      return;

    case DocActionTypes.UPDATE_INBOUNDING_VARIABLES: {
      const inBoundsIdx = draft.views[
        draft.currentViewName
      ].importedServices.findIndex((elem) => elem.serviceGroup == "IN_BOUND");
      draft.views[draft.currentViewName].importedServices[
        inBoundsIdx
      ].services[0].serviceContexts.any.rsMapping.view = action.inbounds;

      return;
    }

    case DocActionTypes.UPDATE_OUTBOUNDING_VARIABLES: {
      const outBoundsIdx = draft.views[
        draft.currentViewName
      ].importedServices.findIndex((elem) => elem.serviceGroup == "OUT_BOUND");
      draft.views[draft.currentViewName].importedServices[
        outBoundsIdx
      ].services[0].serviceContexts.any.rqMapping.view = action.outbounds;

      return;
    }

    case DocActionTypes.SET_SELECTED_FIELD:
      draft.currentSelectedFieldId = action.fieldId;
      return;

    case DocActionTypes.SET_SELECTED_RADIO:
      draft.currentSelectedRadioId = action.fieldId;
      return;

    case DocActionTypes.SET_DOC_LOADED:
      draft.formLoading = false;
      return;

    case DocActionTypes.UPDATE_APP_FIELDS:
      for (const field in action.payload) {
        draft.views[draft.currentViewName].appFields[field] =
          action.payload[field];
      }
      return;

    case DocActionTypes.UPDATE_APP_FIELD:
      draft.views[draft.currentViewName].appFields[
        draft.currentSelectedFieldId
      ] = action.payload;
      return;

    case DocActionTypes.UPDATE_APP_FIELD_TAB_ORDER:
      draft.views[draft.currentViewName].appFields[
        action.payload.name
      ].tabOrder = action.payload.order;

      return;

    case DocActionTypes.UPDATE_FIELD_FILTER:
      draft.views[draft.currentViewName].appFields[
        draft.currentSelectedFieldId
      ].filter = action.payload;

      return;

    case DocActionTypes.SET_DOC_LOADED_FAILURE:
      draft.formLoading = false;
      draft.loadDocFailure = true;
      return;

    case DocActionTypes.UPDATE_FORM_PROPS:
      for (const field in action.payload) {
        if (!(draft.doc[field] instanceof Object)) {
          draft.doc[field] = action.payload[field];
        } else {
          draft.doc[field] = {
            ...draft.doc[field],
            ...action.payload[field],
          };
        }
      }

    case DocActionTypes.SET_DEFAULT_FORM_PROPS:
      for (const field in action.payload) {
        if (!(draft.doc[field] instanceof Object)) {
          draft.doc[field] = action.payload[field];
        } else {
          draft.doc[field] = {
            ...draft.doc[field],
            ...action.payload[field],
          };
        }
      }

      return;

    case DocActionTypes.UPDATE_FORM_PRINTER:
      draft.doc.printSetting = action.payload;

      return;

    case DocActionTypes.SET_PAGE_COUNT:
      draft.pageCount = action.payload;

      return;

    case DocActionTypes.RESET_STATE:
      Object.keys(initialState).forEach((key) => {
        draft[key] = initialState[key];
      });

      return;

    case DocActionTypes.SET_IMPORTED_SERVICES:
      draft.views[draft.currentViewName].importedServices =
        action.importedServices;

      return;

    case DocActionTypes.SET_CURRENT_RQ_MAPPING:
      draft.views[draft.currentViewName].importedServices[
        draft.currentService.groupIndex
      ].services[draft.currentService.serviceIndex].serviceContexts[
        draft.currentService.context
      ].rqMapping.view = action.rqViewMapping;

      return;

    case DocActionTypes.SET_CURRENT_RS_MAPPING:
      draft.views[draft.currentViewName].importedServices[
        draft.currentService.groupIndex
      ].services[draft.currentService.serviceIndex].serviceContexts[
        draft.currentService.context
      ].rsMapping.view = action.rsViewMapping;

      return;

    case DocActionTypes.SET_CURRENT_RS_CTX:
      draft.views[draft.currentViewName].importedServices[
        draft.currentService.groupIndex
      ].services[draft.currentService.serviceIndex].serviceContexts[
        draft.currentService.context
      ].rsMapping.ctx = action.rsCtxMapping;
      return;

    case DocActionTypes.SET_CURRENT_RQ_CTX:
      draft.views[draft.currentViewName].importedServices[
        draft.currentService.groupIndex
      ].services[draft.currentService.serviceIndex].serviceContexts[
        draft.currentService.context
      ].rqMapping.ctx = action.rqCtxMapping;
      return;

    case DocActionTypes.SET_CURRENT_RS_ENV:
      draft.views[draft.currentViewName].importedServices[
        draft.currentService.groupIndex
      ].services[draft.currentService.serviceIndex].serviceContexts[
        draft.currentService.context
      ].rsMapping.env = action.rsEnvMapping;
      return;

    case DocActionTypes.SET_CURRENT_RQ_ENV:
      draft.views[draft.currentViewName].importedServices[
        draft.currentService.groupIndex
      ].services[draft.currentService.serviceIndex].serviceContexts[
        draft.currentService.context
      ].rqMapping.env = action.rqEnvMapping;
      return;

    case DocActionTypes.UPDATE_SERVICE_CONTEXT:
      draft.views[draft.currentViewName].importedServices[
        draft.currentService.groupIndex
      ].services[draft.currentService.serviceIndex].serviceContexts[
        action.contextName
      ] = action.mapping;

      return;

    case DocActionTypes.SET_CURRENT_CONTEXT:
      draft.currentService = {
        ...draft.currentService,

        context: action.context,
      };

      return;

    case DocActionTypes.SET_HTML_ELEMENTS_LIST:
      draft.htmlElements = action.payload.htmlElements;
      return;

    case DocActionTypes.UPDATE_HTML_ELEMENT:
      const htmlElement = draft.htmlElements.find(
        (e) => e.dataFieldName === action.payload.id
      );
      if (htmlElement) {
        const iframe =
          document.getElementsByName("formMainFrame")[0]["contentDocument"];
        if (iframe)
          iframe
            .getElementById(htmlElement.id)
            .setAttribute(action.payload.key, action.payload.value);
      }
      return;

    case DocActionTypes.UPDATE_FIELD_TYPE:
      draft.views[draft.currentViewName].appFields[
        draft.currentSelectedFieldId
      ].fieldDataType.type = action.data.type;
      draft.views[draft.currentViewName].appFields[
        draft.currentSelectedFieldId
      ].fieldDataType.format = action.data.format;
      draft.views[draft.currentViewName].appFields[
        draft.currentSelectedFieldId
      ].fieldDataType.maxLength = action.data.maxLength;
      return;

    case DocActionTypes.UPDATE_HELP_VALUE:
      draft.views[draft.currentViewName].appFields[
        draft.currentSelectedFieldId
      ].helpLtrFile = action.data;

      draft.views[draft.currentViewName].appFields[
        draft.currentSelectedFieldId
      ].helpLtrChoice = action.choice;

      return;

    case DocActionTypes.SET_SERVICE:
      draft.currentService = action.service;

      return;

    case DocActionTypes.UPDATE_FIELD_TRIGGERS:
      draft.views[draft.currentViewName].appFields[
        draft.currentSelectedFieldId
      ].fieldTriggers = action.payload;

      return;

    case DocActionTypes.UPDATE_FIELD_TRIGGER_CUSTOM_HANDLER:
      draft.views[draft.currentViewName].appFields[
        draft.currentSelectedFieldId
      ].fieldTriggers.customHandler = action.payload;

      return;

    case DocActionTypes.ADD_FIELD_TRIGGER:
      draft.views[draft.currentViewName].appFields[
        draft.currentSelectedFieldId
      ].fieldTriggers = action.payload;

      return;

    case DocActionTypes.ADD_VIEW:
      draft.views[action.payload.viewName] = action.payload;

      draft.doc.onLoad.push(action.payload.viewName);

      return;

    case DocActionTypes.UPDATE_FORM_CATEGORIES:
      draft.doc.catalogs = action.categories;

      return;

    case DocActionTypes.UPDATE_FORM_ACTIONS:
      draft.views[draft.currentViewName][action.payload.formView].services =
        action.payload.services;

      return;

    case DocActionTypes.UPDATE_FORM_TRIGGER:
      draft.views[draft.currentViewName][
        action.payload.formView
      ].customHandler = action.payload.customTrigger;

      return;

    case DocActionTypes.DELETE_SERVICE_CONTEXT:
      delete draft.views[draft.currentViewName].importedServices[
        draft.currentService.groupIndex
      ].services[draft.currentService.serviceIndex].serviceContexts[
        action.payload
      ];

      return;

    case DocActionTypes.DELETE_IMPORTED_SERVICE: {
      try {
        const currentView = draft.currentViewName;
        const currentImportedServices =
          draft.views[currentView].importedServices;
        let currentOnLoadServices = draft.views[currentView].onLoad.services;
        let currentOnSubmitServices =
          draft.views[currentView].onSubmit.services;
        // Clear from imported services
        // First remove service
        const group = currentImportedServices.find(
          (group) => group.serviceGroup === action.payload.serviceGroup
        );
        const newGroupServices = group.services.filter(
          (s) => s.name !== action.payload.service
        );
        // Second remove group
        draft.views[currentView].importedServices =
          currentImportedServices.filter(
            (service) => service.serviceGroup !== action.payload.serviceGroup
          );
        // if there are remained services in group, Add the group again
        if (newGroupServices.length > 0) {
          group.services = newGroupServices;
          draft.views[currentView].importedServices.push(group);
        }

        // Clear from AppFields triggers
        const fields = draft.views[currentView].appFields;
        const fieldNames = Object.keys(fields);
        fieldNames.map((field) => {
          const triggers = Object.keys(fields[field].fieldTriggers);
          triggers.map((trigger) => {
            const newSVC = fields[field].fieldTriggers[trigger].services.filter(
              (svc) =>
                svc.name !== action.payload.service &&
                svc.serviceGroup !== action.payload.serviceGroup
            );
            draft.views[currentView].appFields[field].fieldTriggers[
              trigger
            ].services = newSVC;
          });
        });

        // Clear from onLoad and onsubmit
        draft.views[currentView].onLoad.services = currentOnLoadServices.filter(
          (s) => s.serviceGroup !== action.payload.serviceGroup
        );
        draft.views[currentView].onSubmit.services =
          currentOnSubmitServices.filter(
            (s) => s.serviceGroup !== action.payload.serviceGroup
          );

        //change current service
        draft.currentService = null;
      } catch (error) {
        console.warn(
          "Error trying to delete a service group from the form: ",
          action.payload
        );
      }

      return;
    }

    case DocActionTypes.UPDATE_TRIGGER_POST_HOOK:
      draft.views[draft.currentViewName].importedServices[
        draft.currentService.groupIndex
      ].services[draft.currentService.serviceIndex].serviceContexts[
        draft.currentService.context
      ].postHandler = action.payload;

      return;

    case DocActionTypes.UPDATE_TRIGGER_PRE_HOOK:
      draft.views[draft.currentViewName].importedServices[
        draft.currentService.groupIndex
      ].services[draft.currentService.serviceIndex].serviceContexts[
        draft.currentService.context
      ].preHandler = action.payload;

      return;

    case DocActionTypes.UPDATE_FORM_WORK_AREAS:
      draft.doc.workAreas = action.workAreas;

      return;

    case DocActionTypes.UPDATE_FORM_PERMISSIONS:
      draft.doc.permissions = action.permissions;

      return;

    case DocActionTypes.UPDATE_FORM_PRIVILEGE_GROUPS:
      draft.doc.privilegeGroups = action.privilegeGroups;

      return;

    case DocActionTypes.UPDATE_VIEW_MIRROR_GROUPS:
      draft.views[draft.currentViewName].mirrorGroups = action.mirrorGroups;

      return;
    case DocActionTypes.UPDATE_ONLOAD:
      draft.doc.onLoad = action.onLoad;

      return;
    case DocActionTypes.UPDATE_VIEWS:
      draft.views = action.views;
      return;

    // Popup actions
    case DocActionTypes.ADD_POPUP_FIELD:
      draft.views[draft.currentViewName].appFields[action.appField.name] =
        action.appField;
      draft.views[draft.currentViewName].popupDetails.data.push(
        action.popupField
      );
      return;

    case DocActionTypes.REMOVE_POPUP_FIELD:
      if (draft.views[draft.currentViewName].appFields[action.fieldId]) {
        const currentView = draft.views[draft.currentViewName];
        delete currentView.appFields[action.fieldId];
        currentView.popupDetails.data = currentView.popupDetails.data.filter(
          (f) => f.id !== action.fieldId
        );
      }

      return;

    case DocActionTypes.UPDATE_POPUP_FIELD: {
      const fieldIndex = draft.views[
        draft.currentViewName
      ].popupDetails.data.findIndex((f) => f.id === action.popupFieldData.id);

      if (fieldIndex !== -1) {
        draft.views[draft.currentViewName].popupDetails.data[fieldIndex] =
          structuredClone(action.popupFieldData);
      }

      return;
    }

    case DocActionTypes.UPDATE_POPUP_STAGE_DATA: {
      draft.views[draft.currentViewName].popupDetails.stageDefaultInfo =
        structuredClone(action.payload);
      return;
    }

    case DocActionTypes.UPDATE_POPUP_TABLE_CONTROLS: {
      draft.views[draft.currentViewName].title = action.title;
      draft.views[draft.currentViewName].pbutton = action.pbutton;
      draft.views[draft.currentViewName].sbutton = action.sbutton;
      return;
    }

    case DocActionTypes.UPDATE_POPUP_TABLE_DATA: {
      draft.views[draft.currentViewName].popupDetails.table = structuredClone(
        action.payload
      );
      return;
    }

    case DocActionTypes.UPDATE_APP_FIELD_DISPLAY_NAME: {
      try {
        // Update appfield portion
        const view = draft.views[action.payload.viewName];
        const popupData = view.popupDetails.data ?? [];
        const popupDataField = popupData.find(
          (f) => f.id === action.payload.fieldId
        );
        const appFields = view.appFields;
        const appField = appFields[action.payload.fieldId];

        if (appField && popupDataField) {
          appField.displayName = action.payload.newName;
          popupDataField.fieldDisplayName = action.payload.newName;
        } else {
          throw "Could not update field display name";
        }
        // Update popup detail portion
      } catch (error) {
        console.warn(error);
      }

      return;
    }
  }
}, initialState);
